import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import {
  TenantConfigService,
  UseCaseFlowConfig,
} from "../../../types/tenantConfig";
import { resolveFlowConfig } from "../../../util/categoryConfig";

/** A booking, as far as its category is concerned. */
type BookedItems =
  | { items?: { group?: { productCategory?: string | null } }[] }
  | null
  | undefined;

/**
 * The check-in/check-out flow configured for one use case, or `null` when that
 * use case has no flow.
 *
 * THE SINGLE READ PATH in this app. Callers must not reach into
 * `config.checkInCheckOutFlow.useCases[x]` themselves, so an absent,
 * malformed or half-written entry can never be mistaken for "enabled" — the
 * same discipline `useDirectBookingFlow` follows for the booking-flow map, and
 * mirrored server-side by `resolveUseCaseFlow` in both new backends.
 *
 * There is deliberately **no tenant-level fallback**: the flow is configured
 * per use case and nothing else (PO, 2026-09-03). `null` therefore covers every
 * "no flow here" case at once — no block, no `useCases`, no entry for this use
 * case, or `enabled` not literally `true`.
 *
 * **Why this is safe for existing tenants.** `UseOrderPage` consults the flow
 * for `venue` and `apartment` only; every other service (`sup`, `rental`,
 * `storage`, `p2p`, `parcel`, `keys`) routes to its own dedicated
 * `UseOrderFlow` and never reads this config at all. So the legacy tenants'
 * usage flows — Segmenta's SUP flow, Vonovia's and DHU's — are structurally
 * untouched by this map, not merely defaulted off. And for venue/apartment,
 * an absent entry yields `null`, which lands on `BookingOnly`: exactly what a
 * tenant without the old tenant-level `enabled` flag got before.
 *
 * Strict on `enabled === true` because the value arrives from the public tenant
 * config, which is built from untrusted JSONB: a truthy-but-not-true value must
 * not switch a customer-facing flow on.
 */
/**
 * CATEGORY FIRST, USE CASE AS THE FALLBACK (ADR-0026 §B).
 *
 * WHOSE category: the selected booking's, unless `booking` names another one.
 * A screen that renders several bookings at once (the ticket carousel) passes
 * each card's own booking — reading the selected one there gave every
 * neighbouring card the selected booking's flow, so a category without
 * check-in showed "booked" in its window, and one with check-in ignored it.
 * The parameter is named `booking` on purpose: `ownBookingNotSelected.test.ts`
 * accepts a call that passes it and flags one that does not.
 *
 * The category comes from the BOOKING, not the URL. Every consumer of this
 * hook is post-booking and is entered from an order link, which carries no
 * `?category=` — so the URL-based lookup the booking flows use would resolve
 * to `undefined` on every call here, and the category branch would be dead
 * code reading as a working feature (the #1361 shape). The order response
 * carries `group.productCategory` for exactly this reason.
 *
 * A category that configures its own flow wins over its use case, INCLUDING
 * when it configures it as off: that is how a municipality runs check-in for
 * its meeting rooms and not for its barbecue site, and it is why the projector
 * serves a disabled category block rather than dropping it.
 *
 * `resolveFlowConfig` keeps the strict `enabled === true` test this hook has
 * always applied, on both sides.
 */
export const useUseCaseFlow = (
  useCase: TenantConfigService | string | undefined | null,
  booking?: BookedItems,
): UseCaseFlowConfig | null =>
  useSelector((state: RootState) => {
    if (!useCase) return null;
    return (
      resolveFlowConfig(
        state.tenantConfig?.config,
        useCase,
        bookedCategory(booking ?? state.booking?.selectedBooking),
      ) ?? null
    );
  });

/**
 * The category of the booking being used, or `undefined`.
 *
 * Read off the FIRST item. An order already carries one `use_case` for all of
 * its items, so it carries one category in practice too; taking the first is
 * the same assumption `service` has always made, stated rather than hidden. A
 * booking with no items, no group or no category yields `undefined`, which is
 * the fallback — the behaviour every tenant has today.
 */
const bookedCategory = (booking: BookedItems): string | undefined =>
  booking?.items?.[0]?.group?.productCategory ?? undefined;

/** Whether this use case runs the generic flow — the boolean-only view, for
 *  call sites that only branch on it. */
export const useIsUseCaseFlowEnabled = (
  useCase: TenantConfigService | string | undefined | null,
): boolean => useUseCaseFlow(useCase) !== null;

export default useUseCaseFlow;
